import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, usePropertyField } from '@vibesites/runtime/react';
import { EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/** Formats a phone string into XXX.XXX.XXXX (dot-separated, matching the footer). Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
}

export function ResidentAccess() {
  const portalUrl = (usePropertyField('property_resident_portal_url') as string) || '';
  const callTrackingPhone = (usePropertyField('property_call_tracking_phone') as string) || '';
  const defaultPhone = (usePropertyField('property_phone') as string) || '';
  const phone = callTrackingPhone || defaultPhone;

  const portalTitle = useContentToken('residentaccess.portal_title');
  const portalCta = useContentToken('residentaccess.portal_cta_label');
  const helpTitle = useContentToken('residentaccess.help_title');
  const helpCtaLabel = useContentToken('residentaccess.help_cta_label');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="ra-section">
      <div ref={sectionRef} className="reveal-fade-up ra-container">
        {/* Intro */}
        <div className="ra-intro">
          <RichTextContent
            path="residentaccess.intro"
            label="Intro Text"
            defaultValue="<p>Sign in to your resident account to manage your payments, view maintenance requests, and stay updated on community news.</p>"
            as="div"
            className="ra-intro-text"
          />
        </div>

        <div className="ra-grid">
          {/* Resident Login Card */}
          <div className="ra-card">
            <EditableToken path="residentaccess.portal_title" as="h3" className="ra-card-title">
              {portalTitle || 'Resident Login'}
            </EditableToken>
            {portalUrl ? (
              <a
                href={portalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ra-btn inline-flex items-center justify-center text-center max-w-full px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
              >
                <EditableToken path="residentaccess.portal_cta_label" as="span">
                  {portalCta || 'Login'}
                </EditableToken>
              </a>
            ) : (
              <span className="ra-btn ra-btn--disabled inline-flex items-center justify-center text-center max-w-full px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all">
                <EditableToken path="residentaccess.portal_cta_label" as="span">
                  {portalCta || 'Portal URL Not Available'}
                </EditableToken>
              </span>
            )}
          </div>

          {/* Need Help Card */}
          <div className="ra-card">
            <EditableToken path="residentaccess.help_title" as="h3" className="ra-card-title">
              {helpTitle || 'Need Help? Call Us'}
            </EditableToken>
            <a
              href={phone ? `tel:${phone}` : '#'}
              className="dniphone dniphonehref ra-phone"
            >
              <EditableToken path="residentaccess.help_cta_label" as="span">
                {formatPhone(helpCtaLabel || phone)}
              </EditableToken>
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .ra-section {
          padding: 2.5rem 0 3.5rem;
          background: white;
        }
        @media (min-width: 768px) {
          .ra-section { padding: 3.5rem 0 4.5rem; }
        }
        /* Full-bleed row: the container spans the viewport with generous side
           gutters (matching the Apply Now page) rather than a narrow centered
           column. */
        .ra-container {
          max-width: 100rem;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        @media (min-width: 640px) { .ra-container { padding: 0 2.5rem; } }
        @media (min-width: 1024px) { .ra-container { padding: 0 4rem; } }
        @media (min-width: 1280px) { .ra-container { padding: 0 6rem; } }
        /* Tablet: pull the row closer to the viewport edges so the two cards
           have more room. */
        @media (min-width: 768px) and (max-width: 1023px) { .ra-container { padding: 0 1.5rem; } }

        .ra-intro {
          text-align: center;
          max-width: 44rem;
          margin: 0 auto 2.5rem;
        }
        @media (min-width: 768px) { .ra-intro { margin-bottom: 3rem; } }
        .ra-intro-text {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.7;
          color: var(--vibe-color-text-muted);
        }
        @media (min-width: 768px) { .ra-intro-text { font-size: 1.125rem; } }
        .ra-intro-text a { color: var(--vibe-color-primary); text-decoration: underline; }

        .ra-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 768px) {
          /* minmax(0, 1fr) lets tracks shrink below content min-width so the
             two cards can't force horizontal overflow at tablet widths. */
          .ra-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 2.5rem; }
        }
        /* Tablet: tighten the gap between the two cards. */
        @media (min-width: 768px) and (max-width: 1023px) { .ra-grid { gap: 1.5rem; } }

        .ra-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 2rem;
          min-height: 18rem;
          padding: 3rem 2.5rem;
          background: #ffffff;
          border: 1px solid #ececef;
          border-radius: 0;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
          transition: box-shadow 0.25s, transform 0.25s;
        }
        /* Mobile: compact the card into a more landscape shape — drop the tall
           min-height, tighten the top/bottom padding, and pull the title and
           CTA closer together. */
        @media (max-width: 767px) {
          .ra-card {
            min-height: 0;
            gap: 1rem;
            padding: 1.75rem 2rem;
          }
        }
        @media (min-width: 768px) { .ra-card { padding: 3.5rem 4rem; } }
        /* Tablet keeps tighter horizontal padding so the title/CTA have room;
           the generous padding is reserved for desktop. The tall min-height is
           dropped and the gap tightened so the narrower tablet cards read as
           landscape (like desktop) rather than square. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .ra-card { min-height: 0; gap: 1.5rem; padding: 2.5rem 2rem; }
        }
        @media (min-width: 1024px) { .ra-card { padding: 4rem 5rem; } }
        .ra-card:hover {
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transform: translateY(-2px);
        }

        .ra-card-title {
          /* Hard-coded Poppins semibold — not the theme heading token. */
          font-family: "Poppins", sans-serif;
          /* Mobile: keep the title small enough to stay on a single line
             inside the card at narrow widths. */
          font-size: 1.375rem;
          font-weight: 600;
          line-height: 1.2;
          color: var(--vibe-color-text);
          margin: 0;
        }
        @media (min-width: 768px) { .ra-card-title { font-size: 2rem; } }
        /* Tablet: dial the title back so it stays on a single line. */
        @media (min-width: 768px) and (max-width: 1023px) { .ra-card-title { font-size: 1.5rem; } }

        /* Solid primary pill — matches the site-wide CTA treatment
           (FloorPlansIntro, VirtualTourIntro, header .nav-cta-btn). */
        .ra-btn {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
          text-decoration: none;
          cursor: pointer;
          max-width: 100%;
        }
        .ra-btn:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }

        .ra-btn--disabled,
        .ra-btn--disabled:hover {
          background: var(--vibe-color-border, #e5e7eb);
          border-color: var(--vibe-color-border, #e5e7eb);
          color: var(--vibe-color-text-muted);
          cursor: default;
          opacity: 0.7;
          transform: none;
          box-shadow: none;
        }

        /* Phone rendered as bold accent text (not a button).
           Font matches .ra-card-title (hard-coded Poppins). */
        .ra-phone {
          font-family: "Poppins", sans-serif;
          font-size: 1.75rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: #000;
          text-decoration: none;
          transition: opacity 0.2s ease;
        }
        @media (min-width: 768px) { .ra-phone { font-size: 2rem; } }
        .ra-phone:hover { opacity: 0.8; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentAccess',
  displayName: 'Resident Access',
  tokens: {
    'residentaccess.intro': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue:
        '<p>Sign in to your resident account to manage your payments, view maintenance requests, and stay updated on community news.</p>',
    },
    'residentaccess.portal_title': {
      label: 'Login Card Title',
      type: 'text',
      defaultValue: 'Resident Login',
    },
    'residentaccess.portal_cta_label': {
      label: 'Login CTA Label',
      type: 'text',
      defaultValue: 'Login',
    },
    'residentaccess.help_title': {
      label: 'Help Card Title',
      type: 'text',
      defaultValue: 'Need Help? Call Us',
    },
    'residentaccess.help_cta_label': {
      label: 'Phone CTA Label',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '',
    },
  },
};

(ResidentAccess as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ResidentAccess;
